import { useTranslation } from "react-i18next";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Role } from "../../../core/models/entities/Role";
import { enviroment } from "../../../enviroment";
import { useProfileContext } from "../../../contexts/ProfileContext";
import {
  isSharingApiUnavailable,
  useLogoSharingRepository,
} from "../../../data/repositories/LogoSharingRepository";
import { LogoSharingOrgRef } from "../../../core/models/entities/LogoSharing";
import { TypographyR } from "../Typographies/SectionHeaderTypography";
import { BadgeChip } from "../OrgShell/BadgeChip";

// Small "Sharing" block on the logo detail popup (admin-only, behind the
// logo-sharing flag): who can see this logo and why — directly assigned,
// via sharing relations, or because the logo is global (endpoint 8).
export function LogoSharingInfoSection({ logo }: { logo: BaseLogo }) {
  const { role } = useProfileContext();
  const isAdmin = role === Role.Admin;
  const isEnabled = enviroment.logoSharingEnabled && isAdmin;

  const { useLogoSharing } = useLogoSharingRepository();
  const sharing = useLogoSharing(logo.id, { enabled: isEnabled });

  const { t } = useTranslation();

  if (!isEnabled) return null;

  if (sharing.isError) {
    // Before the Etape 2 API is live every call answers 404 — show a quiet
    // hint instead of an error; any other failure is silently omitted (the
    // block is purely informational).
    return isSharingApiUnavailable(sharing.error) ? (
      <TypographyR className="text-gray-400">
        <b>{t("logoSharing.logoSectionTitle")} </b>
        {t("logoSharing.apiNotActive")}
      </TypographyR>
    ) : null;
  }

  if (sharing.isLoading || !sharing.data) return null;

  const { direct, viaRelations, global } = sharing.data;
  const isShared = global || direct.length > 0 || viaRelations.length > 0;

  return (
    <div className="space-y-1">
      <TypographyR className="flex items-center gap-x-1.5">
        <b>{t("logoSharing.logoSectionTitle")} </b>
        {global && (
          <BadgeChip tone="blue">{t("logoSharing.logoGlobal")}</BadgeChip>
        )}
        {!isShared && (
          <span className="text-gray-500">
            {t("logoSharing.logoNotShared")}
          </span>
        )}
      </TypographyR>
      <OrgRefLine label={t("logoSharing.logoDirect")} orgs={direct} />
      <OrgRefLine
        label={t("logoSharing.logoViaRelations")}
        orgs={viaRelations}
      />
    </div>
  );
}

function OrgRefLine({
  label,
  orgs,
}: {
  label: string;
  orgs: LogoSharingOrgRef[];
}) {
  if (orgs.length === 0) return null;
  return (
    <TypographyR className="text-xs text-gray-600">
      <b>{label}: </b>
      {orgs.map((org) => org.name || `#${org.orgId}`).join(", ")}
    </TypographyR>
  );
}
